import { createContext, ReactNode, useState, useContext } from 'react';
import { parse, addDays } from 'date-fns';
import { api } from '../services/api';

interface TableContextProps {
  children: ReactNode;
}

type TableContextData = {
  faturaParcelas: Fatura_Parc[];
  faturaParcela: Fatura_Parc;
  setFaturaParcelas: React.Dispatch<React.SetStateAction<Fatura_Parc[]>>;
  faturaError: string;
  page: string;
  isParcela: boolean;
  showTable: boolean;
  tableSelected: number;
  showDeleteModal: boolean;
  showEditModal: boolean;
  showCashModal: boolean;
  idParcela: idParcela;
  setShowTable: React.Dispatch<React.SetStateAction<boolean>>;
  setTableSelected: React.Dispatch<React.SetStateAction<number>>;
  openDeleteModal: (
    fatura_parc?: Fatura_Parc,
    documento?: number,
    nroparc?: number,
    isParcela?: boolean,
    valorParcela?: number,
  ) => void;
  openEditModal: (documento?: number, nroparc?: number) => void;
  openCashModal: (documento?: number, nroparc?: number) => void;
  openParcelas: (fatura_parc: Fatura_Parc) => void;
  criarFatura: (
    fatura: IFaturas,
    proxVencimento: number,
  ) => Promise<Fatura_Parc>;
  setPageParcela: (page: string) => void;
  listarFaturas: (faturas_parc: Fatura_Parc[]) => void;
  setFaturasParcelas: (fatura_parc: Fatura_Parc) => void;
  setMudarFaturaParcela: (fatura_parc: Fatura_Parc) => void;
  criarFaturasAPI: (
    fatura: IFaturas,
    cpf: string,
    proxVencimento: number,
    uf?: string,
  ) => Promise<any>;
  hasFaturaError: (error: boolean, text?: string) => void;
};

type IParcela = {
  valorparc: number;
  valorPago?: number;
  vencimento: string;
  pagamento?: string;
  documento: number;
  nroparc: number;
  CGC_CPF?: string;
};

export type IFaturas = {
  documento: number;
  tipodoc: number;
  n_inicial?: number;
  n_final?: number;
  qtdeparc: number;
  data1parc: string;
  valorparc: number;
  valorfatura?: number;
};

interface idParcela {
  documento?: number;
  nroparc?: number;
  valorParcela?: number;
}

export interface Fatura_Parc {
  fatura: IFaturas;
  parcelas: IParcela[];
}

const TableContext = createContext({} as TableContextData);

export function TableProvider({ children }: TableContextProps) {
  const [faturaParcelas, setFaturaParcelas] = useState<Fatura_Parc[]>([]);
  const [faturaParcela, setFaturaParcela] = useState<Fatura_Parc>(
    {} as Fatura_Parc,
  );

  const [showTable, setShowTable] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCashModal, setShowCashModal] = useState(false);
  const [tableSelected, setTableSelected] = useState(0);
  const [idParcela, setIdParcela] = useState<idParcela>({} as idParcela);
  const [isParcela, setIsParcela] = useState(false);
  const [page, setPage] = useState('');

  const [faturaError, setFaturaError] = useState('');

  async function openParcelas(fatura_parc: Fatura_Parc) {
    setFaturaParcela(fatura_parc);
    setTableSelected(fatura_parc.fatura.documento);
    setShowTable(true);
  }

  async function criarFatura(
    fatura: IFaturas,
    proxVencimento: number,
  ): Promise<any> {
    const parcelas: IParcela[] = [];

    for (let index = 0; index < fatura.qtdeparc; index++) {
      const parcela1 = parse(fatura.data1parc, 'dd/MM/yyyy', new Date())
        .toLocaleDateString()
        .split('/')
        .reverse()
        .join('-');

      const dataParcelaAnterior = new Date(parcelas[index - 1]?.vencimento);

      parcelas.push({
        valorparc: fatura.valorparc,
        vencimento:
          index === 0
            ? parcela1
            : addDays(dataParcelaAnterior, proxVencimento + 1)
                .toLocaleDateString()
                .split('/')
                .reverse()
                .join('-'),
        documento: fatura.documento,
        nroparc: fatura.n_inicial ? fatura.n_inicial + index : index + 1,
      });
    }

    const faturaNova: IFaturas = {
      documento: fatura.documento,
      tipodoc: fatura.tipodoc,
      qtdeparc: fatura.qtdeparc,
      data1parc: fatura.data1parc.split('/').reverse().join('-'),
      valorparc: fatura.valorparc,
    };

    setFaturaParcelas([...faturaParcelas, { fatura: faturaNova, parcelas }]);
    openParcelas({ fatura: faturaNova, parcelas });

    return;
  }

  async function criarFaturasAPI(
    fatura: IFaturas,
    cpf: string,
    proxVencimento: number,
    uf?: string,
  ): Promise<any> {
    const parcelas = [];

    for (let index = 0; index < fatura.qtdeparc; index++) {
      const parcela1 = parse(fatura.data1parc, 'dd/MM/yyyy', new Date())
        .toLocaleDateString()
        .split('/')
        .reverse()
        .join('-');

      const dataParcelaAnterior: Date = new Date(
        parcelas[index - 1]?.vencimento,
      );

      parcelas.push({
        valorparc: fatura.valorparc,
        vencimento:
          index === 0
            ? parcela1
            : addDays(dataParcelaAnterior, proxVencimento)
                .toLocaleDateString()
                .split('/')
                .reverse()
                .join('-'),
        documento: fatura.documento,
        nroparc: fatura.n_inicial ? fatura.n_inicial + index : index + 1,
      });
    }

    const fatura_parc = {
      fatura: {
        documento: fatura.documento,
        tipodoc: fatura.tipodoc,
        qtdeparc: fatura.qtdeparc,
        data1parc: fatura.data1parc.split('/').reverse().join('-'),
        valorparc: fatura.valorparc,
      },
      parcelas,
    };

    await api
      .post('/fatura/criar', {
        fatura_parcelas: [fatura_parc],
        cgc_cpf: cpf,
        credor: '685',
        uf: uf,
      })
      .then(
        (response: {
          status: number;
          data: { faturas_parc: Fatura_Parc[] };
        }) => {
          if (response.status === 200) {
            listarFaturas(response.data.faturas_parc);
            hasFaturaError(false);
            return;
          }
        },
      )
      .catch(() => {
        hasFaturaError(true, 'Erro ao cadastrar fatura. Tente novamente.');
      });
  }

  function hasFaturaError(error: boolean, text?: string) {
    if (error) {
      if (text) {
        setFaturaError(text);
      } else {
        setFaturaError('Preencha todos os campos corretamente');
      }
    } else {
      setFaturaError('');
    }
  }

  function listarFaturas(faturas_parc: Fatura_Parc[]) {
    setFaturaParcelas(faturas_parc);
  }

  function setMudarFaturaParcela(fatura_parc: Fatura_Parc) {
    const faturas = faturaParcelas.filter(
      (fatura) => fatura.fatura.documento !== fatura_parc.fatura.documento,
    );
    listarFaturas([...faturas, fatura_parc]);
    setFaturasParcelas(fatura_parc);
  }

  function setFaturasParcelas(fatura_parc: Fatura_Parc) {
    setFaturaParcela(fatura_parc);
  }

  function openDeleteModal(
    fatura_parc?: Fatura_Parc,
    documento?: number,
    nroparc?: number,
    isParcela?: boolean,
    valorParcela?: number,
  ) {
    setIsParcela(false);
    if (isParcela && valorParcela) {
      setIsParcela(true);
      setIdParcela({
        documento: documento,
        nroparc: nroparc,
        valorParcela: valorParcela,
      });
    }
    if (fatura_parc) {
      setFaturasParcelas(fatura_parc);
    }
    setShowDeleteModal(!showDeleteModal);
  }

  function openEditModal(
    documento?: number,
    nroparc?: number,
    isParcela?: boolean,
  ) {
    setShowEditModal(!showEditModal);
    if (isParcela) {
      setIsParcela(!isParcela);
    }
    setIdParcela({ documento: documento, nroparc: nroparc });
  }

  function openCashModal(documento?: number, nroparc?: number) {
    setShowCashModal(!showCashModal);
    setIdParcela({ documento: documento, nroparc: nroparc });
  }
  function setPageParcela(page: string) {
    setPage(page);
  }

  return (
    <TableContext.Provider
      value={{
        faturaParcelas,
        faturaParcela,
        setFaturaParcelas,
        showTable,
        tableSelected,
        setTableSelected,
        showDeleteModal,
        showEditModal,
        showCashModal,
        openDeleteModal,
        openEditModal,
        openCashModal,
        idParcela,
        criarFatura,
        openParcelas,
        setShowTable,
        page,
        setPageParcela,
        listarFaturas,
        isParcela,
        setFaturasParcelas,
        criarFaturasAPI,
        setMudarFaturaParcela,
        hasFaturaError,
        faturaError,
      }}
    >
      {children}
    </TableContext.Provider>
  );
}

export const useInvoicesTable = () => useContext(TableContext);
